/**
 * P0-PM-005: Post-Install Guidance Modal
 * Shows "What's Next?" guidance after successful BoB installation
 */

import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ExternalLink, FileText, Settings, Sparkles } from 'lucide-react';

interface PostInstallGuidanceProps {
  bobName: string;
  instanceId: string;
  onClose: () => void;
}

export function PostInstallGuidance({ bobName, instanceId, onClose }: PostInstallGuidanceProps) {
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md" closeLabel="Close installation complete dialog">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <Sparkles className="h-6 w-6 text-blue-400" aria-hidden="true" />
            <DialogTitle>Installation Complete!</DialogTitle>
          </div>
        </DialogHeader>

        <div className="space-y-4">
          <p className="text-sm text-slate-300">
            <strong className="text-slate-100">{bobName}</strong> is now active on your Odoo instance.
          </p>

          <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4">
            <h3 className="font-semibold text-slate-100 mb-3">What's Next?</h3>

            <ol className="space-y-3 text-sm text-slate-300">
              <li className="flex gap-2">
                <span className="text-blue-400 font-semibold">1.</span>
                <span>Log in to your Odoo instance to see the new BoB in action</span>
              </li>
              <li className="flex gap-2">
                <span className="text-blue-400 font-semibold">2.</span>
                <span>Check the BoB's documentation for configuration options</span>
              </li>
              <li className="flex gap-2">
                <span className="text-blue-400 font-semibold">3.</span>
                <span>Explore the new features in your Odoo dashboard</span>
              </li>
            </ol>
          </div>

          <div className="space-y-2">
            <Button asChild variant="outline" className="w-full" size="sm">
              <a
                href={`/docs/bobs/${bobName.toLowerCase().replace(/\s+/g, '-')}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <FileText className="h-4 w-4 mr-2" aria-hidden="true" />
                View Documentation
                <ExternalLink className="h-3 w-3 ml-2" aria-hidden="true" />
              </a>
            </Button>

            <Button asChild variant="outline" className="w-full" size="sm">
              <a
                href={`/client/instances/${instanceId}`}
                onClick={onClose}
              >
                <Settings className="h-4 w-4 mr-2" aria-hidden="true" />
                Go to Instance Settings
              </a>
            </Button>
          </div>
        </div>

        <DialogFooter>
          <Button onClick={onClose} className="w-full">
            Got it, thanks!
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
